import React, { useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import TrustHint from "@/components/TrustHint";
import { Plus, Search, FileText, Calendar, Tag, Trash2, ExternalLink, FolderLock, Pencil, AlertTriangle, Eye, BellRing } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  title: "", category: "ausweise", description: "", tags: "",
  expiry_date: "", reminder_date: "", file: null,
};

function severityVisual(d, lang) {
  // Single source of truth: severity comes from backend
  switch (d.severity) {
    case "expired":
      return { label: "Abgelaufen", cls: "ag-badge-red", icon: AlertTriangle };
    case "urgent":
      return { label: `${d.days_left} Tage`, cls: "ag-badge-amber", icon: BellRing };
    case "soon":
      return { label: `${d.days_left} Tage`, cls: "ag-badge-slate", icon: Calendar };
    default:
      return null;
  }
}

function isImage(ct) { return (ct || "").startsWith("image/"); }
function isPdf(ct) { return (ct || "") === "application/pdf"; }

export default function VaultPage() {
  const { t, lang } = useI18n();
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewSrc, setPreviewSrc] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/documents", { params: { category, q: search || undefined } });
      setDocs(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); }, [category]);

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Bitte eine Datei auswählen."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("description", form.description);
    fd.append("tags", form.tags);
    fd.append("expiry_date", form.expiry_date);
    fd.append("reminder_date", form.reminder_date);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument gespeichert.");
      setUploadOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload fehlgeschlagen"));
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (d) => {
    setEditDoc(d);
    setEditForm({
      title: d.title || "",
      category: d.category || "sonstiges",
      description: d.description || "",
      tags: (d.tags || []).join(", "),
      expiry_date: d.expiry_date || "",
      reminder_date: d.reminder_date || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...editForm,
      tags: editForm.tags.split(",").map((s) => s.trim()).filter(Boolean),
      expiry_date: editForm.expiry_date || null,
      reminder_date: editForm.reminder_date || null,
    };
    try {
      await api.patch(`/documents/${editDoc.id}`, body);
      toast.success("Änderungen gespeichert.");
      setEditOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Konnte nicht speichern"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Dokument wirklich entfernen?")) return;
    try {
      await api.delete(`/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      toast.success("Dokument entfernt.");
    } catch {
      toast.error("Konnte Dokument nicht entfernen");
    }
  };

  const openPreview = async (d) => {
    if (!d.storage_path) {
      toast("Demo-Eintrag: keine echte Datei hinterlegt.");
      return;
    }
    setPreviewDoc(d);
    setPreviewOpen(true);
    try {
      const r = await fetch(`${API_BASE}/documents/${d.id}/download`, { credentials: "include" });
      const blob = await r.blob();
      setPreviewSrc(URL.createObjectURL(blob));
    } catch {
      toast.error("Vorschau konnte nicht geladen werden");
    }
  };

  useEffect(() => {
    if (!previewOpen && previewSrc) {
      URL.revokeObjectURL(previewSrc);
      setPreviewSrc(null);
      setPreviewDoc(null);
    }
  }, [previewOpen, previewSrc]);

  const cats = Object.keys(t.vault.categories);

  return (
    <AppLayout>
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{t.nav.vault}</div>
          <h1 className="font-serif text-5xl text-ag-ink mb-2">{t.vault.title}</h1>
          <p className="text-slate-600 max-w-xl">{t.vault.subtitle}</p>
          <div className="mt-4">
            <TrustHint testId="vault-trust-hint">{t.trustHints.vault}</TrustHint>
          </div>
        </div>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ag-ink hover:bg-slate-800 h-11 rounded-xl" data-testid="vault-upload-btn">
              <Plus className="h-4 w-4 mr-2" /> {t.vault.upload}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{t.vault.upload}</DialogTitle>
              <DialogDescription>Verschlüsselt gespeichert. Nur Sie haben Zugriff.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submitUpload} className="space-y-4 pt-2" data-testid="vault-upload-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.titleField}</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="vault-form-title" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.category}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="vault-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => (<SelectItem key={c} value={c}>{t.vault.categories[c]}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.description}</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="vault-form-description" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.expiry}</Label>
                  <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="vault-form-expiry" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.reminder}</Label>
                  <Input type="date" value={form.reminder_date} onChange={(e) => setForm({ ...form, reminder_date: e.target.value })} data-testid="vault-form-reminder" className="mt-1.5 h-11" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.tags}</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="vault-form-tags" className="mt-1.5 h-11" placeholder="Versicherung, Wichtig" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.file}</Label>
                <Input type="file" required onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="vault-form-file" className="mt-1.5" />
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="ghost" onClick={() => setUploadOpen(false)}>{t.vault.cancel}</Button>
                <Button type="submit" disabled={uploading} data-testid="vault-form-submit" className="bg-ag-ink hover:bg-slate-800">{uploading ? "..." : t.vault.save}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="vault-search"
            placeholder={t.vault.search}
            className="pl-10 h-11 bg-white"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="md:w-56 h-11 bg-white" data-testid="vault-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.vault.all}</SelectItem>
            {cats.map((c) => (<SelectItem key={c} value={c}>{t.vault.categories[c]}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {docs.length === 0 ? (
        <div className="ag-card p-12 text-center" data-testid="vault-empty">
          <div className="h-14 w-14 rounded-2xl bg-slate-100 mx-auto flex items-center justify-center mb-4">
            <FolderLock className="h-6 w-6 text-slate-500" strokeWidth={1.4} />
          </div>
          <p className="text-slate-600 max-w-md mx-auto">{t.vault.empty}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {docs.map((d) => {
            const eb = severityVisual(d, lang);
            return (
              <div key={d.id} className="ag-card ag-card-hover p-6" data-testid={`vault-doc-${d.id}`}>
                <div className="flex items-start justify-between mb-4 gap-2">
                  <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-ag-ink" strokeWidth={1.5} />
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className="ag-badge ag-badge-slate">{t.vault.categories[d.category] || d.category}</span>
                    {eb && (
                      <span className={`ag-badge ${eb.cls}`} data-testid={`expiry-${d.id}`}>
                        <eb.icon className="h-3 w-3" /> {eb.label}
                      </span>
                    )}
                  </div>
                </div>
                <div className="font-serif text-xl text-ag-ink mb-1 leading-tight">{d.title}</div>
                {d.description && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{d.description}</p>}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 mb-4">
                  {d.expiry_date && (
                    <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Ablauf: {new Date(d.expiry_date).toLocaleDateString(lang)}</span>
                  )}
                  {d.reminder_date && (
                    <span className="inline-flex items-center gap-1"><BellRing className="h-3 w-3" /> Erinnerung: {new Date(d.reminder_date).toLocaleDateString(lang)}</span>
                  )}
                  {d.tags?.length > 0 && (
                    <span className="inline-flex items-center gap-1"><Tag className="h-3 w-3" /> {d.tags.join(", ")}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => openPreview(d)}
                    data-testid={`vault-doc-preview-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Eye className="h-3 w-3" /> Ansehen
                  </button>
                  <button
                    onClick={() => openEdit(d)}
                    data-testid={`vault-doc-edit-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Pencil className="h-3 w-3" /> Bearbeiten
                  </button>
                  <button
                    onClick={() => remove(d.id)}
                    data-testid={`vault-doc-delete-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-slate-500 hover:text-red-600 transition-colors ml-auto"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Dokument bearbeiten</DialogTitle>
            <DialogDescription>Änderungen wirken sich sofort auf Ihr Dashboard aus.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={submitEdit} className="space-y-4 pt-2" data-testid="vault-edit-form">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.titleField}</Label>
                <Input required value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="vault-edit-title" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.category}</Label>
                <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cats.map((c) => (<SelectItem key={c} value={c}>{t.vault.categories[c]}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.description}</Label>
                <Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.expiry}</Label>
                  <Input type="date" value={editForm.expiry_date || ""} onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })} data-testid="vault-edit-expiry" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.reminder}</Label>
                  <Input type="date" value={editForm.reminder_date || ""} onChange={(e) => setEditForm({ ...editForm, reminder_date: e.target.value })} className="mt-1.5 h-11" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">{t.vault.tags}</Label>
                <Input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} className="mt-1.5 h-11" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>{t.vault.cancel}</Button>
                <Button type="submit" disabled={saving} data-testid="vault-edit-submit" className="bg-ag-ink hover:bg-slate-800">{saving ? "..." : "Speichern"}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{previewDoc?.title}</DialogTitle>
            <DialogDescription>{previewDoc?.original_filename || ""}</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-100 rounded-xl overflow-hidden min-h-[60vh] flex items-center justify-center">
            {!previewSrc ? (
              <div className="text-sm text-slate-500">Wird geladen…</div>
            ) : isImage(previewDoc?.content_type) ? (
              <img src={previewSrc} alt={previewDoc?.title} className="max-h-[70vh] object-contain" />
            ) : isPdf(previewDoc?.content_type) ? (
              <iframe src={previewSrc} title={previewDoc?.title} className="w-full h-[70vh]" />
            ) : (
              <a href={previewSrc} download={previewDoc?.original_filename || "datei"} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ag-ink text-white">
                <ExternalLink className="h-4 w-4" /> Herunterladen
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
